import { CustomFieldDefinitionActionAvailability } from '@/api/queries/customField/useGetFieldset';
import {
  FormValues,
  TCreateEditCustomFieldSidebarContentProps,
} from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import {
  CheckboxGroupField,
  Field,
  Input,
  NumberInput,
  RadioGroupField,
  Select,
  Textarea,
} from '@payaca/ambrosia';

import CustomFieldGroupOptionsInput from '@/ui/components/customFieldGroupControl/components/CustomFieldGroupOptionsInput';

import { UploadType } from '@payaca/custom-fields';
import { MAX_UPLOAD_LIMIT } from '@payaca/custom-fields/constants/index';
import { camelize } from '@payaca/utilities/stringUtilities';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { appendPath, PathToFieldsetChild } from '../types';
import { capMultiSelectLimit } from '../utils/capMultiSelectLimit';
import { getDepthFromFieldPath } from '../utils/getDepthFromFieldPath';
import { getTypeOptionsForField } from '../utils/getTypeOptionsForField';
import NestedFieldsetChildrenEditor from './NestedFieldsetChildrenEditor';
import { useFieldsetChildField } from './useFieldsetChildField';

export type TProps = {
  isDestructivelyUpdateable: CustomFieldDefinitionActionAvailability['isDestructivelyUpdateable'];
  optionsInUse?: CustomFieldDefinitionActionAvailability['optionsInUse'];
  isPersisted: boolean;
  /** Field path for nested fields (e.g., "children.0" or "children.0.children.1"). Undefined for root field. */
  fieldPath?: PathToFieldsetChild;
  /** Callback to open the nested field editor sidebar (for depth 2 fields) */
  onOpenNestedFieldEditor?: (parentIndex: number, nestedIndex: number) => void;
  /** Whether the nested field editor sidebar is currently open */
  isNestedFieldEditorOpen?: boolean;
  /** Full action availability array for per-child removal checks in nested fieldsets */
  actionAvailability?: CustomFieldDefinitionActionAvailability[];
  /** Root fieldset identifier (e.g. "projects") */
  fieldsetIdentifier?: string;
} & Pick<TCreateEditCustomFieldSidebarContentProps, 'allCurrentIdentifiers'>;

const CreateEditCustomFieldFieldset: FC<TProps> = (props) => {
  const {
    isDestructivelyUpdateable,
    optionsInUse,
    allCurrentIdentifiers,
    fieldPath,
    isPersisted,
    onOpenNestedFieldEditor,
    isNestedFieldEditorOpen,
    actionAvailability,
    fieldsetIdentifier,
  } = props;

  const depth = getDepthFromFieldPath(fieldPath);

  const { control, watch, setError, clearErrors } =
    useFormContext<FormValues>();

  const type = watch(appendPath(fieldPath, 'type'));

  const typeOptions = getTypeOptionsForField(depth, isPersisted);

  return (
    <>
      <Controller
        render={({ field: { value, onChange, name } }) => {
          const identificationValue = value;
          return (
            <>
              <Field name={`${name}.label`}>
                <Field.Label>Label</Field.Label>

                <Input
                  value={identificationValue?.label}
                  onChange={(newLabel) => {
                    if (isPersisted) {
                      onChange({ ...identificationValue, label: newLabel });
                      return;
                    }

                    let newIdentifier = camelize(
                      newLabel.replace(/[^a-zA-Z0-9 ]/g, '')
                    ).slice(0, 250);

                    if (allCurrentIdentifiers.includes(newIdentifier)) {
                      newIdentifier = `${newIdentifier}${uuid().slice(0, 5)}`;
                    }

                    onChange({
                      identifier: newIdentifier,
                      label: newLabel,
                    });
                  }}
                />
              </Field>

              <Field name={`${name}.identifier`}>
                <Field.Label>Internal name</Field.Label>
                <Field.HelperText>
                  This unique identifier will be used for integrations.
                </Field.HelperText>

                <Input
                  disabled={isPersisted}
                  value={identificationValue?.identifier}
                  onChange={(v) => {
                    if (allCurrentIdentifiers.includes(v)) {
                      setError(
                        appendPath(fieldPath, 'identification.identifier'),
                        {
                          type: 'custom',
                          message: 'This identifier is already in use',
                        }
                      );
                    } else {
                      clearErrors(
                        appendPath(fieldPath, 'identification.identifier')
                      );
                    }

                    onChange({ ...identificationValue, identifier: v });
                  }}
                />
              </Field>
            </>
          );
        }}
        name={appendPath(fieldPath, 'identification')}
        control={control}
      />

      <Controller
        render={({ field: { value, onChange, name } }) => {
          return (
            <Field name={name}>
              <Field.Label>Description</Field.Label>
              <Field.HelperText>
                This description will guide users on how to use this field.
              </Field.HelperText>
              <Textarea
                value={value ?? undefined}
                onChange={onChange}
                rows={1}
                autoHeight={true}
              />
            </Field>
          );
        }}
        name={appendPath(fieldPath, 'description')}
        control={control}
      />

      {!!typeOptions.length && fieldPath && (
        <Controller
          name={fieldPath}
          control={control}
          render={({ field: { value, onChange, name } }) => {
            const fieldValue = value;
            return (
              <Field name={`${name}.type`}>
                <Field.Label>Type</Field.Label>
                <Select
                  disabled={
                    !isDestructivelyUpdateable.result ||
                    (isPersisted && fieldValue.type === 'fieldset')
                  }
                  options={typeOptions}
                  value={fieldValue.type}
                  onChange={(newType) => {
                    const baseValue = {
                      identification: fieldValue.identification,
                      description: fieldValue.description,
                      type: newType,
                    };

                    switch (newType) {
                      case 'select':
                      case 'multi-select':
                        onChange({
                          ...baseValue,
                          options: [],
                          displayMode: 'dropdown',
                        });
                        break;
                      case 'upload':
                        onChange({
                          ...baseValue,
                          limit: 1,
                          uploadType: ['image'],
                        });
                        break;
                      case 'fieldset':
                        onChange({
                          ...baseValue,
                          children: [],
                        });
                        break;
                      default:
                        onChange(baseValue);
                        break;
                    }
                  }}
                />
              </Field>
            );
          }}
        />
      )}

      {type === 'select' && (
        <AdditionalSelectInput
          fieldPath={fieldPath}
          optionsInUse={optionsInUse}
        />
      )}

      {type === 'multi-select' && (
        <AdditionalMultiSelectInput
          fieldPath={fieldPath}
          optionsInUse={optionsInUse}
        />
      )}

      {type === 'upload' && (
        <AdditionalUploadInput
          isDestructivelyUpdateable={isDestructivelyUpdateable}
          allCurrentIdentifiers={allCurrentIdentifiers}
          fieldPath={fieldPath}
          isPersisted={isPersisted}
        />
      )}

      {type === 'fieldset' && !!fieldPath && (
        <NestedFieldsetChildrenEditor
          parentFieldName={fieldPath}
          isPersisted={isPersisted}
          actionAvailability={actionAvailability}
          fieldsetIdentifier={fieldsetIdentifier}
          parentFieldIdentifier={
            watch(appendPath(fieldPath, 'identification'))?.identifier || ''
          }
          onOpenNestedFieldEditor={onOpenNestedFieldEditor}
          isNestedFieldEditorOpen={isNestedFieldEditorOpen}
        />
      )}
    </>
  );
};

export default CreateEditCustomFieldFieldset;

type OptionsInputProps = Pick<TProps, 'fieldPath' | 'optionsInUse'>;

const AdditionalSelectInput: FC<OptionsInputProps> = (props) => (
  <OptionsAndDisplayModeInputs
    {...props}
    expectedType="select"
    buttonsDisplayMode="radio"
  />
);

const AdditionalMultiSelectInput: FC<OptionsInputProps> = (props) => (
  <>
    <OptionsAndDisplayModeInputs
      {...props}
      expectedType="multi-select"
      buttonsDisplayMode="checkbox"
    />
    <MultiSelectLimitInput fieldPath={props.fieldPath} />
  </>
);

/**
 * The options editor and display-mode picker, which select and multi-select
 * share. They differ only in the value their Buttons mode stores.
 */
const OptionsAndDisplayModeInputs: FC<
  OptionsInputProps & {
    expectedType: 'select' | 'multi-select';
    buttonsDisplayMode: 'radio' | 'checkbox';
  }
> = (props) => {
  const { fieldPath, optionsInUse, expectedType, buttonsDisplayMode } = props;

  const fieldsetChildField = useFieldsetChildField({
    fieldPath,
    expectedType,
  });

  if (!fieldsetChildField) return null;

  const { namePrefix, control } = fieldsetChildField;

  return (
    <>
      <Controller
        name={appendPath(namePrefix, 'options')}
        control={control}
        render={({ field }) => {
          return (
            <Field name={field.name}>
              <Field.Label>Options</Field.Label>

              <CustomFieldGroupOptionsInput
                options={field.value}
                optionsInUse={optionsInUse}
                onChange={(newOptions) => field.onChange(newOptions)}
              />
            </Field>
          );
        }}
      />

      <Controller
        name={appendPath(namePrefix, 'displayMode')}
        control={control}
        render={({ field: { value, onChange, name } }) => {
          return (
            <Field name={name}>
              <Field.Label>Display mode</Field.Label>
              <Field.HelperText>
                How this field is displayed when filling in the form.
              </Field.HelperText>
              <RadioGroupField
                horizontal
                options={[
                  { label: 'Dropdown', value: 'dropdown' },
                  { label: 'Buttons', value: buttonsDisplayMode },
                ]}
                value={value}
                onChange={onChange}
              />
            </Field>
          );
        }}
      />
    </>
  );
};

const MultiSelectLimitInput: FC<{ fieldPath?: PathToFieldsetChild }> = (
  props
) => {
  const { fieldPath } = props;

  const { watch } = useFormContext<FormValues>();

  const currentOptions = fieldPath
    ? watch(appendPath(fieldPath, 'options'))
    : undefined;

  const fieldsetChildField = useFieldsetChildField({
    fieldPath,
    expectedType: 'multi-select',
  });

  if (!fieldsetChildField) return null;

  const { namePrefix, control } = fieldsetChildField;

  const optionCount = (currentOptions || []).filter(
    (option) => !!option
  ).length;

  return (
    <Controller
      name={appendPath(namePrefix, 'limit')}
      control={control}
      render={({ field }) => {
        return (
          <Field name={field.name}>
            <Field.Label>Most that can be chosen</Field.Label>
            <Field.HelperText>
              Leave empty to allow every option to be chosen.
            </Field.HelperText>
            <NumberInput
              value={capMultiSelectLimit(field.value, optionCount) ?? null}
              max={optionCount || undefined}
              onChange={(newLimit) => field.onChange(newLimit || undefined)}
            />
          </Field>
        );
      }}
    />
  );
};

const AdditionalUploadInput: FC<TProps> = (props) => {
  const { fieldPath } = props;

  const fieldsetChildField = useFieldsetChildField({
    fieldPath,
    expectedType: 'upload',
  });

  if (!fieldsetChildField) return null;

  const { namePrefix, control } = fieldsetChildField;

  return (
    <>
      <Controller
        name={appendPath(namePrefix, 'uploadType')}
        control={control}
        render={({ field }) => {
          return (
            <Field name={field.name}>
              <Field.Label>Accepted file types</Field.Label>
              <Field.HelperText>
                Which types of file can be uploaded to this field.
              </Field.HelperText>
              <CheckboxGroupField
                horizontal
                options={UPLOAD_TYPE_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
              />
            </Field>
          );
        }}
      />

      <Controller
        name={appendPath(namePrefix, 'limit')}
        control={control}
        render={({ field }) => {
          return (
            <Field name={appendPath(namePrefix, 'limit')}>
              <Field.Label>Upload limit</Field.Label>
              <Field.HelperText>
                Number of files that can be uploaded to this field (max{' '}
                {MAX_UPLOAD_LIMIT})
              </Field.HelperText>
              <NumberInput
                value={field.value ?? null}
                onChange={(newLimit) => field.onChange(newLimit || undefined)}
              />
            </Field>
          );
        }}
      />
    </>
  );
};

const UPLOAD_TYPE_OPTIONS = [
  { label: 'Images', value: 'image' },
  { label: 'Documents', value: 'document' },
  { label: 'Videos', value: 'video' },
] satisfies { label: string; value: UploadType }[];
